<template>
  <div class="rc">
    <header class="rc__head">
      <div>
        <router-link class="rc__back" :to="{ name: 'RegistryMonitor', query: registry ? { customer: registry.customer_id } : {} }">← Мониторинг реестров</router-link>
        <h1 class="rc__title">
          Контроль реестра {{ registry && registry.number ? '№ ' + registry.number : '' }}
          <span v-if="registry">от {{ formatDate(registry.planned_load_date) }} · {{ registry.fleet === 'own' ? 'собственный транспорт' : 'наёмный транспорт' }}</span>
        </h1>
        <p v-if="registry" class="rc__lead">
          Подбираются рейсы с погрузкой не позже даты реестра, не включённые в другой реестр.
          Договор: <b>{{ registry.contract_name || 'не задан' }}</b>.
          <template v-if="registry.plan">
            Отправить комплект до <b>{{ formatDate(registry.plan.send_docs_by) }}</b>,
            оригиналы не позже <b>{{ formatDate(registry.plan.originals_deadline) }}</b>,
            оплата <b>{{ formatDate(registry.plan.payment_date) }}</b>.
          </template>
        </p>
      </div>
      <div class="rc__controls">
        <button class="rc__btn" type="button" :disabled="pending" @click="load">{{ pending ? 'Загружаем…' : 'Обновить' }}</button>
        <button class="rc__btn" type="button" :disabled="!summary.in_registry" @click="print">Печатная форма (Excel)</button>
      </div>
    </header>

    <div v-if="error" class="rc__error">{{ error }}</div>
    <div v-if="message" class="rc__ok">{{ message }}</div>

    <div class="rc__kpis">
      <button v-for="k in kpis" :key="k.key" type="button" :class="['kpi', `kpi--${k.tone}`, { 'is-active': quick === k.key }]" @click="quick = quick === k.key ? '' : k.key">
        <span class="kpi__value">{{ k.value }}</span><span class="kpi__label">{{ k.label }}</span>
      </button>
    </div>

    <form class="rc__filters" @submit.prevent="load">
      <label>Погрузка с <input v-model="filters.loading_from" type="date" class="rc__input"></label>
      <label>по <input v-model="filters.loading_to" type="date" class="rc__input"></label>
      <label>Транспорт
        <select v-model="filters.fleet" class="rc__input">
          <option value="">как у реестра</option><option value="hired">наёмный</option><option value="own">собственный</option><option value="all">любой</option>
        </select>
      </label>
      <label>Финансовый статус TMS
        <select v-model="filters.finance_status" class="rc__input">
          <option value="">любой</option><option v-for="s in financeStatuses" :key="s" :value="s">{{ s }}</option>
        </select>
      </label>
      <label>Договор
        <select v-model="filters.contract_terms_id" class="rc__input">
          <option value="">любой</option><option v-for="t in terms" :key="t.id" :value="t.id">{{ t.contract_name }} (с {{ formatDate(t.effective_from) }})</option>
        </select>
      </label>
      <label>Фоновая проверка
        <select v-model="filters.check" class="rc__input">
          <option value="">любая</option><option value="checked">проверен</option><option value="unchecked">не проверен</option>
        </select>
      </label>
      <label>Поиск <input v-model="search" class="rc__input" placeholder="номер, маршрут, перевозчик"></label>
      <button class="rc__btn rc__btn--primary" type="submit">Применить</button>
    </form>

    <div v-if="canEdit" class="rc__bar">
      <span>Выделено: <b>{{ selection.count }}</b> на <b>{{ formatMoney(selection.total) }}</b> ₽ без НДС</span>
      <button class="rc__btn rc__btn--primary" type="button" :disabled="!selectedOutside.length" @click="add">Включить в реестр ({{ selectedOutside.length }})</button>
      <button class="rc__btn" type="button" :disabled="!selectedInside.length" @click="remove">Убрать из реестра ({{ selectedInside.length }})</button>
      <button class="rc__btn" type="button" :disabled="!selected.length" @click="setPackage(true)">Документы собраны</button>
      <button class="rc__btn" type="button" :disabled="!selected.length" @click="setPackage(false)">Снять отметку</button>
      <button class="rc__btn rc__btn--ghost" type="button" @click="selectReady">Выделить готовые</button>
    </div>

    <div class="rc__table-wrap">
      <table class="rc__table">
        <thead>
          <tr>
            <th><input type="checkbox" :checked="allChecked" @change="toggleAll($event.target.checked)"></th>
            <th>Рейс</th><th>Погрузка</th><th>Разгрузка</th><th>Маршрут</th><th>Перевозчик</th>
            <th>Статус</th><th>Фин. статус TMS</th>
            <th>ERP, без НДС</th><th>TMS</th><th>ТМ</th><th>Сверка</th>
            <th>Документы</th><th>Готовность</th><th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="t in visible" :key="t.id" :class="{ 'row--in': t.in_registry, 'row--bad': !t.ready && !t.in_registry }">
            <td><input v-model="selected" type="checkbox" :value="t.id"></td>
            <td><router-link :to="`/trips/${t.id}`" target="_blank"><b>{{ t.number }}</b></router-link>
              <div v-if="t.in_registry" class="tag tag--in">в реестре</div></td>
            <td>{{ formatDate(t.loading_date) }}</td>
            <td>{{ formatDate(t.unloading_date) }}</td>
            <td>{{ t.route || '—' }}</td>
            <td>{{ t.own_fleet ? 'Свой ТС' : (t.carrier_name || '—') }}</td>
            <td>{{ t.status_name || '—' }}</td>
            <td>{{ t.finance_status || '—' }}</td>
            <td class="num">{{ formatMoney(t.prices.erp) }}</td>
            <td class="num">{{ formatMoney(t.prices.tms_efes) }}</td>
            <td class="num">{{ formatMoney(t.prices.transmanager) }}</td>
            <td><span :class="`tag tag--${t.price_check}`">{{ CHECK_LABELS[t.price_check] }}</span></td>
            <td>
              <span :class="['tag', t.docs_complete ? 'tag--ok' : (t.docs_overdue ? 'tag--mismatch' : 'tag--incomplete')]">
                {{ t.docs_complete ? 'собраны' : (t.docs_overdue ? 'срок вышел' : 'ждём') }}
              </span>
              <div v-if="!t.docs_complete && t.docs_deadline" class="muted">до {{ formatDate(t.docs_deadline) }}</div>
            </td>
            <td>
              <span v-if="t.ready" class="tag tag--ok">готов</span>
              <div v-for="p in t.problems" :key="p" class="problem">{{ p }}</div>
            </td>
            <td><button class="rc__btn rc__btn--sm" type="button" @click="docsTrip = t">Документы</button></td>
          </tr>
          <tr v-if="!visible.length && !pending"><td colspan="15" class="rc__placeholder">Рейсов по условиям нет.</td></tr>
        </tbody>
      </table>
    </div>

    <TripDocumentsPanel v-if="docsTrip" :trip="docsTrip" :can-edit="canEdit" @close="docsTrip = null" @changed="load" />
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { registryApi, errorText } from '@/backend/registryApi.js'
import { CHECK_LABELS, filterTrips, formatDate, formatMoney, sumSelected } from './registryLogic.mjs'
import TripDocumentsPanel from './TripDocumentsPanel.vue'

export default {
  name: 'RegistryControlPage',
  components: { TripDocumentsPanel },
  setup() {
    const store = useStore()
    const route = useRoute()
    const id = computed(() => Number(route.params.id))
    const roleName = computed(() => store.state.user.user?.role?.name)
    const canEdit = computed(() => ['superadmin', 'Админ'].includes(roleName.value) || store.getters.hasPermission('registry.edit'))
    const registry = ref(null)
    const trips = ref([])
    const summary = ref({})
    const terms = ref([])
    const financeStatuses = ref([])
    const filters = ref({ loading_from: '', loading_to: '', fleet: '', finance_status: '', contract_terms_id: '', check: '' })
    const search = ref('')
    const quick = ref('')
    const selected = ref([])
    const pending = ref(false)
    const error = ref('')
    const message = ref('')
    const docsTrip = ref(null)

    const QUICK = {
      in_registry: (t) => t.in_registry,
      ready: (t) => t.ready && !t.in_registry,
      price_mismatch: (t) => t.price_check === 'mismatch',
      price_unchecked: (t) => t.price_check === 'incomplete',
      docs_missing: (t) => !t.docs_complete,
      docs_overdue: (t) => t.docs_overdue,
      not_closed: (t) => !t.closed,
    }
    const visible = computed(() => {
      const rows = filterTrips(trips.value, search.value)
      return quick.value ? rows.filter(QUICK[quick.value]) : rows
    })
    const kpis = computed(() => [
      { key: 'in_registry', label: 'в реестре', value: summary.value.in_registry || 0, tone: 'calm' },
      { key: 'ready', label: 'готовы к включению', value: (summary.value.ready || 0) - trips.value.filter((t) => t.ready && t.in_registry).length, tone: 'calm' },
      { key: 'price_mismatch', label: 'расхождение стоимости', value: summary.value.price_mismatch || 0, tone: 'critical' },
      { key: 'price_unchecked', label: 'не проверены (нет данных)', value: summary.value.price_unchecked || 0, tone: 'warning' },
      { key: 'docs_overdue', label: 'восстановить документы', value: summary.value.docs_overdue || 0, tone: 'critical' },
      { key: 'docs_missing', label: 'без отметки о документах', value: summary.value.docs_missing || 0, tone: 'warning' },
      { key: 'not_closed', label: 'не закрыты', value: summary.value.not_closed || 0, tone: 'warning' },
    ])
    const byId = computed(() => new Map(trips.value.map((t) => [t.id, t])))
    const selection = computed(() => sumSelected(trips.value, selected.value))
    const selectedOutside = computed(() => selected.value.filter((i) => byId.value.get(i) && !byId.value.get(i).in_registry))
    const selectedInside = computed(() => selected.value.filter((i) => byId.value.get(i) && byId.value.get(i).in_registry))
    const allChecked = computed(() => visible.value.length > 0 && visible.value.every((t) => selected.value.includes(t.id)))

    async function load() {
      pending.value = true
      error.value = ''
      try {
        const data = await registryApi.candidates(id.value, filters.value)
        registry.value = data.registry
        trips.value = data.trips
        summary.value = data.summary
        financeStatuses.value = data.finance_statuses
        selected.value = selected.value.filter((i) => data.trips.some((t) => t.id === i))
        if (!terms.value.length) terms.value = await registryApi.terms(data.registry.customer_id)
      } catch (e) {
        error.value = errorText(e)
      } finally {
        pending.value = false
      }
    }

    async function run(action, okText) {
      error.value = ''
      message.value = ''
      try {
        await action()
        message.value = okText
        await load()
      } catch (e) {
        error.value = errorText(e)
      }
    }

    const add = () => run(() => registryApi.addTrips(id.value, selectedOutside.value), `Включено рейсов: ${selectedOutside.value.length}`)
    const remove = () => run(() => registryApi.removeTrips(id.value, selectedInside.value), 'Рейсы убраны из реестра')
    const setPackage = (complete) => run(() => registryApi.setPackage(selected.value, complete),
      complete ? 'Отмечено: документы собраны' : 'Отметка о документах снята')
    const toggleAll = (checked) => {
      const ids = visible.value.map((t) => t.id)
      selected.value = checked ? [...new Set([...selected.value, ...ids])] : selected.value.filter((i) => !ids.includes(i))
    }
    const selectReady = () => { selected.value = trips.value.filter((t) => t.ready && !t.in_registry).map((t) => t.id) }
    async function print() {
      try {
        await registryApi.print(id.value, `Реестр ${registry.value?.number || registry.value?.planned_load_date}.xlsx`)
      } catch (e) {
        error.value = errorText(e)
      }
    }

    onMounted(load)
    return {
      CHECK_LABELS, add, allChecked, canEdit, docsTrip, error, filters, financeStatuses, formatDate, formatMoney, kpis,
      load, message, pending, print, quick, registry, remove, search, selectReady, selected, selectedInside,
      selectedOutside, selection, setPackage, summary, terms, toggleAll, visible,
    }
  },
}
</script>

<style scoped>
.rc { padding: 24px 20px 80px; display: flex; flex-direction: column; gap: 14px; color: #111827; }
.rc__head { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; flex-wrap: wrap; }
.rc__back { color: #4f46e5; font-size: 0.85rem; text-decoration: none; }
.rc__title { margin: 4px 0 0; font-size: 1.4rem; font-weight: 700; }
.rc__title span { font-weight: 400; color: #6b7280; font-size: 1rem; }
.rc__lead { margin: 6px 0 0; color: #374151; font-size: 0.9rem; max-width: 900px; }
.rc__controls, .rc__bar { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.rc__bar { position: sticky; top: 0; z-index: 3; background: #eef2ff; border: 1px solid #c7d2fe; border-radius: 12px; padding: 8px 12px; font-size: 0.875rem; }
.rc__btn { border: 1px solid #d1d5db; background: #fff; border-radius: 9999px; padding: 7px 14px; font: inherit; font-size: 0.85rem; cursor: pointer; white-space: nowrap; }
.rc__btn:disabled { opacity: 0.5; cursor: default; }
.rc__btn--primary { background: #4f46e5; border-color: #4f46e5; color: #fff; }
.rc__btn--ghost { background: transparent; }
.rc__btn--sm { padding: 3px 10px; font-size: 0.78rem; }
.rc__input { border: 1px solid #d1d5db; border-radius: 8px; padding: 6px 8px; font: inherit; font-size: 0.85rem; background: #fff; }
.rc__filters { display: flex; flex-wrap: wrap; gap: 10px; align-items: flex-end; }
.rc__filters label { display: flex; flex-direction: column; gap: 4px; font-size: 0.78rem; color: #374151; }
.rc__error { border: 1px solid #fecaca; background: #fef2f2; color: #7f1d1d; border-radius: 10px; padding: 10px 14px; }
.rc__ok { border: 1px solid #bbf7d0; background: #f0fdf4; color: #166534; border-radius: 10px; padding: 10px 14px; }
.rc__kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; }
.kpi { text-align: left; border: 1px solid #e5e7eb; border-radius: 12px; padding: 10px 12px; background: #fff; cursor: pointer; font: inherit; display: flex; flex-direction: column; }
.kpi.is-active { outline: 2px solid #4f46e5; }
.kpi__value { font-size: 1.5rem; font-weight: 700; font-variant-numeric: tabular-nums; }
.kpi__label { font-size: 0.8rem; color: #6b7280; }
.kpi--critical .kpi__value { color: #b91c1c; }
.kpi--warning .kpi__value { color: #b45309; }
.kpi--calm .kpi__value { color: #15803d; }
.rc__table-wrap { overflow: auto; border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; max-height: 70vh; }
.rc__table { border-collapse: collapse; width: 100%; font-size: 0.82rem; }
.rc__table th { position: sticky; top: 0; background: #f9fafb; text-align: left; padding: 8px; border-bottom: 1px solid #e5e7eb; z-index: 1; }
.rc__table td { padding: 6px 8px; border-bottom: 1px solid #f3f4f6; vertical-align: top; }
.row--in td { background: #f0fdf4; }
.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.tag { display: inline-block; border-radius: 9999px; padding: 1px 8px; font-size: 0.75rem; white-space: nowrap; }
.tag--ok { background: #dcfce7; color: #166534; }
.tag--mismatch { background: #fee2e2; color: #b91c1c; }
.tag--incomplete { background: #fef3c7; color: #92400e; }
.tag--in { background: #e0e7ff; color: #3730a3; margin-top: 2px; }
.problem { color: #b45309; font-size: 0.76rem; }
.muted { color: #9ca3af; font-size: 0.75rem; }
.rc__placeholder { text-align: center; color: #6b7280; padding: 24px; }
</style>
